import { supabase } from '../../lib/supabase';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ShieldCheck, CheckCircle, UserPlus, Lock, AlertTriangle, X, Edit,
    Gavel, Activity, FolderTree, Trash2, Shield, Info, Clock, AlertCircle, ChevronRight, ChevronLeft,
    List, Settings, Copy, TrendingUp, Check
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { ACCESS_LEVELS, TICKET_CATEGORIES } from '../../utils/adminUtils';
import { safeParseArray, safeParseObject } from '../../utils/storageUtils';
import { escalationCategories, availableRoles } from '../../utils/escalationConfig';
import ReportedPlayerCard from './Cards/ReportedPlayerCard';
import { useAuth } from '../../contexts/AuthContext';
import { usePunishment } from '../../contexts/PunishmentContext';
import { useTickets } from '../../contexts/TicketContext';
import { getStaffDisplayName } from '../../utils/staffDisplay';

/**
 * Sidebar exclusiva para Staff com ações administrativas
 */
const StaffTicketSidebar = ({
    ticket,
    user,
    isUpdating,
    isClosed,
    handleClaimTicket,
    setIsCloseModalOpen,
    setIsDuplicateModalOpen,
    setAppealAction,
    handleApplyPunishment,
    handlePriorityChange,
    handleCategoryChange,
    handleDeleteTicket,
    handleRegisterAction,
    handleAssignTicket,
    isOpen,
    onToggle,
    allUsers = []
}) => {
    const staffName = getStaffDisplayName(user);
    // State for tabs: 'functions' | 'details' | 'logs'
    const [activeTab, setActiveTab] = useState('functions');
    const [userDetails, setUserDetails] = useState(null);
    const navigate = useNavigate();

    // Escalation Child Check
    const [childEscalationId, setChildEscalationId] = useState(null);

    useEffect(() => {
        const checkEscalation = async () => {
            if (!ticket?.id) return;

            try {
                const { data } = await supabase
                    .from('tickets')
                    .select('id')
                    .eq('linked_ticket_id', ticket.id)
                    .single();

                if (data) {
                    setChildEscalationId(data.id);
                } else {
                    setChildEscalationId(null);
                }
            } catch (err) {
                setChildEscalationId(null);
            }
        };
        checkEscalation();
    }, [isOpen, ticket?.id]);

    const { adminFullUpdateUser } = useAuth();
    const { addPunishment, punishments, updatePunishment, revokePunishment } = usePunishment();
    const { addTicketResponse, updateTicket, createTicket } = useTickets();

    // State to track if user is blocked from tickets
    const [activeTicketBan, setActiveTicketBan] = useState(null);

    // Modal & Reason States
    const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);
    const [blockReason, setBlockReason] = useState('');
    const [isBlocking, setIsBlocking] = useState(false);

    // Escalation State
    const [isEscalationModalOpen, setIsEscalationModalOpen] = useState(false);
    const [escalationData, setEscalationData] = useState({
        category: '',
        subcategory: '',
        description: '',
        role: ''
    });
    const [isEscalating, setIsEscalating] = useState(false);

    const handleEscalateTicket = async () => {
        // Validações
        if (!escalationData.category || !escalationData.subcategory) {
            toast.error('Selecione a categoria e o motivo.');
            return;
        }
        if (!escalationData.description.trim()) {
            toast.error('Preencha a descrição detalhada.');
            return;
        }
        if (!escalationData.role) {
            toast.error('Selecione o departamento responsável.');
            return;
        }

        setIsEscalating(true);
        try {
            const categoryObj = escalationCategories.find(c => c.id === escalationData.category);

            // Dados automáticos
            const charName = ticket.characterName || userDetails?.name || 'Desconhecido';
            const charId = ticket.characterId || userDetails?.cities?.[0]?.playerId || 'N/A';
            const city = ticket.city || userDetails?.cities?.[0]?.city || 'N/A';

            // Montar descrição com dados automáticos
            const fullDescription = `${escalationData.description}\n\n**INFORMAÇÕES AUTOMÁTICAS**\nPersonagem: ${charName} (ID: ${charId})\nCidade: ${city}`;

            // Criar ticket de escalação vinculado
            const escalationTicket = {
                id: `ESC-${Date.now()}`,
                type: 'escalation',
                escalationType: escalationData.category,
                escalationSubtype: escalationData.subcategory,
                title: `[ESCALAÇÃO] ${categoryObj?.label} - ${escalationData.subcategory}`,
                description: fullDescription,
                userId: ticket.userDiscordId || ticket.userId || 'N/A',
                authorName: ticket.userName || ticket.authorName || 'Desconhecido', // Tentar pegar nome
                createdBy: user.discordId,
                createdByName: staffName,
                linkedTicketId: ticket.id, // Vínculo Automático
                accessibleBy: ['staff'],
                assignedRole: escalationData.role,
                status: 'escalado',
                category: 'escalacao_interna',
                city: city, // Garantir cidade
                createdAt: new Date().toISOString(),
                responses: [],
                formData: {
                    characterName: charName,
                    characterId: charId,
                    cities: userDetails?.cities || []
                }
            };

            await createTicket(escalationTicket);

            // Adicionar nota no ticket original
            await addTicketResponse(ticket.id, `🔄 **TICKET ESCALADO**\n\nEste ticket gerou uma escalação para o departamento **${availableRoles.find(r => r.value === escalationData.role)?.label}**.\nID da Escalação: #${escalationTicket.id}`, false, staffName);

            toast.success('Escalação criada com sucesso!');
            setIsEscalationModalOpen(false);
            setEscalationData({ category: '', subcategory: '', description: '', role: '' });

            // Opcional: Redirecionar? O usuário pediu apenas o botão.
        } catch (error) {
            console.error('Erro ao escalar:', error);
            toast.error('Erro ao criar escalação.');
        } finally {
            setIsEscalating(false);
        }
    };

    // Fetch Details Function (Memoized for reuse)
    const fetchDetails = React.useCallback(() => {
        if (!isOpen || (!ticket.userId && !ticket.userDiscordId)) return;

        try {
            const targetId = ticket.userDiscordId || ticket.userId;

            // 1. Find User
            let foundUser = null;
            if (Array.isArray(allUsers)) {
                foundUser = allUsers.find(u =>
                    String(u.discordId) === String(targetId) || String(u.id) === String(targetId)
                );
            }
            if (!foundUser) return;

            // 2. Get User Punishments (Active & History)
            const userPunishments = punishments.filter(p => String(p.userId) === String(foundUser.discordId));

            // Active Ban Check
            const ticketBan = userPunishments.find(p =>
                p.status === 'active' &&
                p.punishmentType === 'Blacklist de Tickets'
            );
            setActiveTicketBan(ticketBan || null);

            // 3. Global History (Actions)
            const historyEvents = [];

            // Tickets Created
            const allTickets = safeParseArray('userTickets'); // Uses utility
            const userTickets = (Array.isArray(allTickets) ? allTickets : []).filter(t =>
                t && (String(t.userId) === String(foundUser.discordId) || String(t.userDiscordId) === String(foundUser.discordId))
            );

            userTickets.forEach(t => {
                historyEvents.push({
                    id: `ticket-${t.id}`,
                    type: 'ticket',
                    date: new Date(t.createdAt || 0),
                    title: `Ticket Criado: #${t.id.slice(-4)}`,
                    description: t.title,
                    icon: 'message',
                    color: 'blue'
                });
            });

            // Punishments & Reviews
            userPunishments.forEach(p => {
                // Applied
                historyEvents.push({
                    id: `punish-${p.id}`,
                    type: 'punishment',
                    date: new Date(p.appliedAt || 0),
                    title: p.punishmentType === 'Blacklist de Tickets' ? 'Bloqueio de Tickets' : p.punishmentType,
                    description: `Aplicado por ${p.appliedBy}`,
                    icon: 'gavel',
                    color: 'red'
                });

                // Revoked
                if (p.status === 'revoked' && p.revokedAt) {
                    historyEvents.push({
                        id: `revoke-${p.id}`,
                        type: 'revoke',
                        date: new Date(p.revokedAt),
                        title: `Desbloqueio / Remoção`,
                        description: `Removido por ${p.revokedBy}`,
                        icon: 'check',
                        color: 'green'
                    });
                }
            });

            // Sort by Date Descending and Take 10
            historyEvents.sort((a, b) => b.date - a.date);
            const recentActions = historyEvents.slice(0, 10);

            // 4. Map Characters
            const characters = (foundUser.cities || []).map(char => {
                const activePunishments = userPunishments.filter(p =>
                    p.status === 'active' &&
                    (p.characterId ? String(p.characterId) === String(char.playerId) : false)
                );
                const lastPunishment = activePunishments.length > 0 ? activePunishments[0] : null;
                return {
                    ...char,
                    activePunishments: activePunishments.length,
                    lastPunishmentAuthor: lastPunishment?.appliedBy
                };
            });

            setUserDetails({
                ...foundUser,
                stats: {
                    totalTickets: userTickets.length,
                    totalPunishments: userPunishments.length
                },
                characters,
                recentActions
            });

        } catch (err) {
            console.error("Error fetching details:", err);
        }
    }, [isOpen, ticket, allUsers, punishments]);


    // Polling Effect
    React.useEffect(() => {
        fetchDetails();
        const interval = setInterval(fetchDetails, 30000);
        return () => clearInterval(interval);
    }, [fetchDetails]);

    const handleApproveLink = async () => {
        if (!userDetails) return;

        // Tentar identificar a cidade pelo ticket (extraído da descrição ou metadados se existir)
        // Se a cidade não estiver explícita, aprova a última pendente encontrada ou todas (arriscado)
        // Como o ticket de validação tem "ID do Usuário" e fala da cidade na descrição,
        // vamos procurar uma cidade PENDENTE no perfil do usuário.

        const pendingCityIndex = userDetails.cities?.findIndex(c =>
            c.status === 'pending_approval' || c.status === 'pending'
        );

        if (pendingCityIndex === -1 || pendingCityIndex === undefined) {
            toast.error('Nenhuma cidade pendente encontrada neste usuário.');
            return;
        }

        const cityToApprove = userDetails.cities[pendingCityIndex];

        if (confirm(`Confirmar aprovação do vínculo para ${cityToApprove.characterName} (${cityToApprove.city})?`)) {
            try {
                // 1. Atualizar User
                const updatedCities = [...userDetails.cities];
                updatedCities[pendingCityIndex] = { ...cityToApprove, status: 'verified' };

                const result = await adminFullUpdateUser(userDetails.discordId, { cities: updatedCities });

                if (!result.success) throw new Error(result.error);

                // 2. Responder e Fechar Ticket
                await addTicketResponse(ticket.id, `✅ **VÍNCULO APROVADO!**\n\nO acesso ao seu personagem **${cityToApprove.characterName}** foi liberado. Agora você já pode abrir tickets de atendimento para esta cidade.`, false, staffName);

                await updateTicket(ticket.id, {
                    status: 'resolvido',
                    auxiliary_status: ['aprovado_manual']
                });

                toast.success('Vínculo aprovado com sucesso!');
                if (onToggle) onToggle(); // Close sidebar or refresh
            } catch (error) {
                console.error(error);
                toast.error('Erro ao aprovar vínculo.');
            }
        }
    };

    const handleRefuseLink = () => {
        setAppealAction({
            type: 'refuse_link',
            title: 'Recusar Vínculo'
        });
    };


    const handleBlockTickets = () => {
        setBlockReason('');
        setIsBlockModalOpen(true);
    };

    const confirmBlockTickets = async () => {
        if (!blockReason || blockReason.trim() === "") {
            toast.error("Motivo é obrigatório.");
            return;
        }

        setIsBlocking(true);
        try {
            await addPunishment({
                userId: ticket.userDiscordId || ticket.userId,
                characterId: null, // Global block usually
                characterName: ticket.characterName || userDetails?.name || 'Bloqueio Tickets',
                city: 'GERAL',
                punishmentType: 'Blacklist de Tickets',
                publicReason: 'Bloqueio administrativo via Ticket',
                internalReason: `[Bloqueio Tickets] ${blockReason} (por ${staffName})`,
                appliedBy: staffName,
                expiresAt: null
            });

            await addTicketResponse(ticket.id, `⛔ **USUÁRIO BLOQUEADO DE TICKETS.**\n\nVocê foi impedido de abrir novos tickets temporariamente.`, false, staffName);
            toast.success('Usuário bloqueado.');
            setIsBlockModalOpen(false);

            // Force immediate update
            setTimeout(() => {
                if (typeof fetchDetails === 'function') fetchDetails();
            }, 500);
        } catch (error) {
            console.error(error);
            toast.error('Erro ao bloquear.');
        } finally {
            setIsBlocking(false);
        }
    };

    const handleUnblockTickets = async () => {
        if (!activeTicketBan) return;
        if (!confirm('Deseja realmente remover o bloqueio de tickets deste usuário?')) return;

        try {
            if (revokePunishment) {
                await revokePunishment(activeTicketBan.id, "Removido via Sidebar de Ticket");
            } else {
                await updatePunishment(activeTicketBan.id, { status: 'revoked', revokedBy: staffName, revokedAt: new Date().toISOString() });
            }

            await addTicketResponse(ticket.id, `✅ **BLOQUEIO DE TICKETS REMOVIDO.**\n\nSeu acesso para criar tickets foi restabelecido.`, false, staffName);
            toast.success('Desbloqueio realizado.');

            // Force immediate update
            setTimeout(() => {
                if (typeof fetchDetails === 'function') fetchDetails();
            }, 500);
        } catch (error) {
            console.error(error);
            toast.error('Erro ao desbloquear.');
        }
    };

    // Fetch user details including characters, stats and history
    // Polling every 30 seconds as requested
    React.useEffect(() => {
        if (isOpen && (ticket.userId || ticket.userDiscordId)) {
            const fetchDetails = () => {
                try {
                    const targetId = ticket.userDiscordId || ticket.userId;

                    // 1. Find User from allUsers (Prop)
                    let foundUser = null;
                    if (Array.isArray(allUsers)) {
                        foundUser = allUsers.find(u =>
                            String(u.discordId) === String(targetId) || String(u.id) === String(targetId)
                        );
                    }

                    if (!foundUser) return; // Wait for load

                    // 2. Get User Punishments from Context (Realtime)
                    // Filter punishments for this user
                    const userPunishments = punishments.filter(p => String(p.userId) === String(foundUser.discordId));

                    // Check for active Ticket Ban
                    const ticketBan = userPunishments.find(p =>
                        p.status === 'active' &&
                        p.punishmentType === 'Blacklist de Tickets'
                    );
                    setActiveTicketBan(ticketBan || null);

                    // 3. Map Characters with Punishment Counts
                    const characters = (foundUser.cities || []).map(char => {
                        // Count active punishments for this character OR generic ones applied to user if no char specified
                        const charPunishments = userPunishments.filter(p =>
                            p.status === 'active' &&
                            (p.characterId ? String(p.characterId) === String(char.playerId) : false)
                        );

                        // Get last punishment author for tooltip
                        const lastPunishment = charPunishments.length > 0 ? charPunishments[0] : null;

                        return {
                            ...char,
                            activePunishments: charPunishments.length,
                            lastPunishmentAuthor: lastPunishment?.appliedBy
                        };
                    });

                    // 4. Update local state
                    // We re-read stats from context as well if needed, or stick to user object
                    setUserDetails({
                        ...foundUser,
                        stats: {
                            totalTickets: foundUser.stats?.totalTickets || 0, // Could fetch real count if needed
                            totalPunishments: userPunishments.length
                        },
                        characters,
                        lastTickets: foundUser.lastTickets || [] // Maintain existing logic or fetch from context tickets if available
                    });

                } catch (err) {
                    console.error("Error fetching details:", err);
                }
            };

            fetchDetails(); // Initial load

            const interval = setInterval(() => {
                // Refresh logic - In a real app we might call an API. 
                // Here we rely on Context being up to date, but we re-run the aggregation every 30s
                // To force a context refresh of the user, we can call adminFullUpdateUser silent?
                // For now, re-aggregating context data is sufficient.
                fetchDetails();
            }, 30000);

            return () => clearInterval(interval);
        }
    }, [isOpen, ticket, allUsers, punishments]);

    // Logic to determine visibility based on category
    const getVisibilityInfo = (category) => {
        if (!category) return { roles: ['Admin'], count: 0 };

        const categoryInfo = TICKET_CATEGORIES[category];
        const authorizedRoles = new Set(['admin', 'head_staff']); // Admin and Head Staff always see everything

        if (categoryInfo) {
            // Add the specific required level
            if (categoryInfo.requiredLevel) {
                authorizedRoles.add(categoryInfo.requiredLevel);
            }
        }

        // Add any role that has 'view_all_tickets' permission
        Object.entries(ACCESS_LEVELS).forEach(([key, level]) => {
            if (level.permissions.includes('view_all_tickets') || level.permissions.includes('all')) {
                authorizedRoles.add(key);
            }
        });

        // Filter valid roles and get their labels
        const activeRoles = Array.from(authorizedRoles)
            .filter(role => ACCESS_LEVELS[role])
            .map(role => ACCESS_LEVELS[role].label);

        // Collect members who have these roles
        const members = [];

        // 1. Author always has access
        if (ticket.user_name || ticket.userName) {
            members.push({
                name: ticket.user_name || ticket.userName,
                role: 'Usuário',
                id: ticket.user_discord_id || ticket.userDiscordId || ticket.user_id || ticket.userId
            });
        }

        // 2. Staff members with authorized roles
        if (Array.isArray(allUsers)) {
            const staffWithAccess = allUsers
                .filter(u => u && u.accessLevel && (authorizedRoles.has(u.accessLevel) || u.accessLevel === 'admin'))
                .filter(u => String(u.discordId || u.id) !== String(ticket.userDiscordId || ticket.userId)) // Don't duplicate if staff is author
                .map(u => ({
                    name: u.staffDetails?.codename || u.staffDetails?.businessData?.codename || u.name || u.username,
                    role: ACCESS_LEVELS[u.accessLevel]?.label || 'Staff',
                    id: u.discordId || u.id
                }));

            members.push(...staffWithAccess);
        }

        // Special check: If no specific roles found (uncategorized?), default to Admin
        if (activeRoles.length === 0) {
            activeRoles.push('Administrador');
        }

        return { roles: activeRoles, count: members.length, members };
    };

    const visibility = getVisibilityInfo(ticket.category);

    return (
        <>
            {/* Mobile Overlay */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black/60 z-40 lg:hidden"
                    onClick={onToggle}
                />
            )}

            {/* Sidebar Container - Glass Effect */}
            <div
                className={`fixed right-0 h-full bg-gradient-to-b from-[#0a0a0a]/95 via-[#050510]/90 to-[#0a0a0a]/95 lg:from-[#0a0a0a]/80 lg:via-[#050510]/75 lg:to-[#0a0a0a]/80 backdrop-blur-2xl border-l border-white/15 z-50 transition-all duration-300 ease-in-out shadow-[-10px_0_50px_rgba(0,0,0,0.5)] ${isOpen ? 'w-full sm:w-[85vw] md:w-[400px] lg:w-[400px]' : 'w-14 md:w-16'} overflow-hidden flex flex-col`}
                style={{
                    backdropFilter: 'blur(24px) saturate(180%)',
                    WebkitBackdropFilter: 'blur(24px) saturate(180%)',
                    top: 'var(--banner-height, 0px)',
                    height: 'calc(100vh - var(--banner-height, 0px))'
                }}
            >
                {/* Header / Toggle Area */}
                <div className={`flex items-center ${isOpen ? 'justify-between px-4 md:px-6' : 'justify-center'} h-16 md:h-20 border-b border-white/5 shrink-0`}>
                    {isOpen && (
                        <div className="flex items-center gap-2 transition-opacity duration-300">
                            <ShieldCheck className="w-4 h-4 md:w-5 md:h-5 text-blue-400" />
                            <span className="text-xs md:text-sm uppercase tracking-[0.2em] font-black text-white">Controle</span>
                        </div>
                    )}
                    <button
                        onClick={onToggle}
                        className="w-10 h-10 md:w-8 md:h-8 flex items-center justify-center rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-all active:scale-95"
                    >
                        {isOpen ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
                    </button>
                </div>

                {/* TABS HEADER (Only visible when open) */}
                {isOpen && (
                    <div className="flex items-center border-b border-white/5 px-3 md:px-6">
                        <button
                            onClick={() => setActiveTab('functions')}
                            className={`flex-1 h-10 md:h-12 text-[9px] md:text-[10px] uppercase font-bold tracking-widest flex items-center justify-center gap-1 md:gap-2 transition-all relative ${activeTab === 'functions' ? 'text-white' : 'text-gray-500 hover:text-gray-300'
                                }`}
                        >
                            <Settings className="w-3 h-3" />
                            <span className="hidden sm:inline">FUNÇÕES</span>
                            <span className="sm:hidden">AÇÕES</span>
                            {activeTab === 'functions' && (
                                <div className="absolute bottom-0 left-0 w-full h-[2px] bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.5)]" />
                            )}
                        </button>
                        <button
                            onClick={() => setActiveTab('details')}
                            className={`flex-1 h-10 md:h-12 text-[9px] md:text-[10px] uppercase font-bold tracking-widest flex items-center justify-center gap-1 md:gap-2 transition-all relative ${activeTab === 'details' ? 'text-white' : 'text-gray-500 hover:text-gray-300'
                                }`}
                        >
                            <UserPlus className="w-3 h-3" />
                            <span className="hidden sm:inline">DETALHES</span>
                            <span className="sm:hidden">INFO</span>
                            {activeTab === 'details' && (
                                <div className="absolute bottom-0 left-0 w-full h-[2px] bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.5)]" />
                            )}
                        </button>
                        <button
                            onClick={() => setActiveTab('logs')}
                            className={`flex-1 h-10 md:h-12 text-[9px] md:text-[10px] uppercase font-bold tracking-widest flex items-center justify-center gap-1 md:gap-2 transition-all relative ${activeTab === 'logs' ? 'text-white' : 'text-gray-500 hover:text-gray-300'
                                }`}
                        >
                            <Clock className="w-3 h-3" />
                            LOGS
                            {activeTab === 'logs' && (
                                <div className="absolute bottom-0 left-0 w-full h-[2px] bg-purple-500 shadow-[0_0_10px_rgba(168,85,247,0.5)]" />
                            )}
                        </button>
                    </div>
                )}


                {/* Scrollable Content */}
                <div className={`flex-1 overflow-y-auto custom-scrollbar p-4 md:p-6 space-y-4 md:space-y-6 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 hidden'}`}>

                    {/* === TAB: FUNÇÕES === */}
                    {activeTab === 'functions' && (
                        <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">

                            {/* 1. SPECIAL ACTIONS (Top Priority) */}
                            <div className="space-y-3">
                                {/* Ver Ticket Pai (Original) - SE ESTE TICKET FOR UMA ESCALAÇÃO */}
                                {(ticket.linkedTicketId || ticket.linked_ticket_id) && (
                                    <Button
                                        onClick={() => navigate(`/staff/tickets/${ticket.linkedTicketId || ticket.linked_ticket_id}`)}
                                        className="w-full h-11 bg-blue-600/10 border border-blue-500/30 text-blue-500 hover:bg-blue-600 hover:text-white rounded-xl font-bold text-[10px] uppercase tracking-wider animate-pulse border-dashed"
                                    >
                                        <TrendingUp className="w-4 h-4 mr-2 rotate-180" /> VER TICKET ORIGINAL
                                    </Button>
                                )}

                                {/* Ver Escalação (Filho) - SE ESTE TICKET GEROU UMA ESCALAÇÃO */}
                                {childEscalationId && (
                                    <Button
                                        onClick={() => navigate(`/staff/tickets/${childEscalationId}`)}
                                        className="w-full h-11 bg-indigo-600/10 border border-indigo-500/30 text-indigo-400 hover:bg-indigo-600 hover:text-white rounded-xl font-bold text-[10px] uppercase tracking-wider animate-pulse border-dashed"
                                    >
                                        <TrendingUp className="w-4 h-4 mr-2" /> VER ESCALAÇÃO GERADA
                                    </Button>
                                )}

                                {/* Validação de Cadastro */}
                                {(ticket.title?.includes('Validação de Vínculo') || ticket.title?.includes('Pré-Registro')) && !isClosed && (
                                    <div className="bg-white/5 rounded-xl p-3 border border-white/5">
                                        <h4 className="text-[10px] font-bold text-gray-500 uppercase tracking-widest pl-1 mb-3">Validação de Cadastro</h4>
                                        <div className="grid grid-cols-2 gap-2">
                                            <Button
                                                onClick={handleRefuseLink}
                                                className="bg-red-500/10 border border-red-500/20 text-red-500 hover:bg-red-500 hover:text-white rounded-xl h-10 font-bold text-[10px] uppercase tracking-wider"
                                            >
                                                <X className="w-3.5 h-3.5 mr-2" /> NEGAR
                                            </Button>
                                            <Button
                                                onClick={handleApproveLink}
                                                className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 hover:bg-emerald-500 hover:text-white rounded-xl h-10 font-bold text-[10px] uppercase tracking-wider"
                                            >
                                                <CheckCircle className="w-3.5 h-3.5 mr-2" /> APROVAR
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                {/* Decisão de Recurso / Punição */}
                                {(
                                    ticket.category?.includes('punicao') ||
                                    ticket.category?.includes('contestar') ||
                                    ticket.title?.toUpperCase().includes('CONTESTAÇÃO') ||
                                    ticket.title?.toUpperCase().includes('RECURSO') ||
                                    ticket.title?.toUpperCase().includes('PUNIÇÃO')
                                ) && !isClosed && (
                                        <div className="bg-white/5 rounded-xl p-3 border border-white/5">
                                            <h4 className="text-[10px] font-bold text-gray-500 uppercase tracking-widest pl-1 mb-3">Decisão de Recurso</h4>
                                            <div className="grid grid-cols-3 gap-2">
                                                <Button
                                                    size="sm"
                                                    onClick={() => setAppealAction({ type: 'deny', title: 'Negar Contestação' })}
                                                    className="bg-red-500/10 border border-red-500/20 text-red-500 hover:bg-red-500 hover:text-white rounded-xl h-12 flex-col gap-1 p-1 shadow-none"
                                                >
                                                    <X className="w-3.5 h-3.5" />
                                                    <span className="text-[8px] font-black tracking-tighter">NEGAR</span>
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    onClick={() => setAppealAction({ type: 'accept', title: 'Aceitar Recurso' })}
                                                    className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 hover:bg-emerald-500 hover:text-white rounded-xl h-12 flex-col gap-1 p-1 shadow-none"
                                                >
                                                    <CheckCircle className="w-3.5 h-3.5" />
                                                    <span className="text-[8px] font-black tracking-tighter">ACEITAR</span>
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    onClick={() => setAppealAction({ type: 'modify', title: 'Alterar Punição' })}
                                                    className="bg-blue-500/10 border border-blue-500/20 text-blue-500 hover:bg-blue-500 hover:text-white rounded-xl h-12 flex-col gap-1 p-1 shadow-none"
                                                >
                                                    <Edit className="w-3.5 h-3.5" />
                                                    <span className="text-[8px] font-black tracking-tighter">ALTERAR</span>
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                            </div>

                            {/* 2. AÇÕES DE RESPOSTA */}
                            <div className="space-y-4">
                                <h4 className="text-[10px] font-bold text-gray-500 uppercase tracking-widest pl-1 border-b border-white/5 pb-2">Ações de Resposta</h4>

                                {/* Pegar Ticket Button */}
                                <Button
                                    onClick={handleClaimTicket}
                                    disabled={isUpdating || isClosed || ticket.assignedTo === user.discordId}
                                    className={`w-full h-12 font-black uppercase tracking-widest text-[11px] rounded-2xl shadow-xl transition-all border-none flex items-center justify-center gap-2 ${ticket.assignedTo === user.discordId
                                        ? 'bg-emerald-500/20 text-emerald-400 cursor-default'
                                        : 'bg-gradient-to-r from-emerald-600 to-emerald-500 hover:scale-[1.02] shadow-emerald-500/20 text-white'
                                        }`}
                                >
                                    {ticket.assignedTo === user.discordId ? <CheckCircle className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
                                    {isUpdating ? 'PROCESSANDO...' : ticket.assignedTo === user.discordId ? 'VOCÊ ASSUMIU' : 'PEGAR TICKET'}
                                </Button>

                                {/* Grid Actions */}
                                <div className="grid grid-cols-2 gap-3">
                                    {/* Encerrar */}
                                    {!isClosed ? (
                                        <Button
                                            variant="destructive"
                                            onClick={() => {
                                                toast.warning("🔔 LEMBRETE: Salve provas importantes na nuvem permanentemente antes de encerrar o ticket!", {
                                                    duration: 6000,
                                                    id: 'close-warning'
                                                });
                                                setIsCloseModalOpen(true);
                                            }}
                                            className="h-11 bg-red-600/10 border border-red-500/30 text-red-500 hover:bg-red-600 hover:text-white shadow-lg shadow-red-500/10 rounded-xl font-bold text-[10px] uppercase tracking-wider transition-all"
                                        >
                                            <Lock className="w-3.5 h-3.5 mr-2" /> Encerrar
                                        </Button>
                                    ) : (
                                        // Abrir Ticket (Reopen) - High Ranks Only
                                        ['admin', 'head_staff', 'head_suporte', 'head_moderacao', 'analista_qualidade'].includes(user.accessLevel) && (
                                            <Button
                                                onClick={() => setIsCloseModalOpen('reopen')}
                                                className="h-11 bg-blue-600/10 border border-blue-500/30 text-blue-500 hover:bg-blue-600 hover:text-white shadow-lg shadow-blue-500/10 rounded-xl font-bold text-[10px] uppercase tracking-wider transition-all"
                                            >
                                                <Lock className="w-3.5 h-3.5 mr-2" /> ABRIR
                                            </Button>
                                        )
                                    )}

                                    {/* Repetida */}
                                    {!isClosed && (
                                        <Button
                                            variant="outline"
                                            onClick={() => setIsDuplicateModalOpen(true)}
                                            className="h-11 bg-yellow-500/5 border border-yellow-500/30 text-yellow-500 hover:bg-yellow-500/10 rounded-xl font-bold text-[10px] uppercase tracking-wider"
                                        >
                                            <AlertTriangle className="w-3.5 h-3.5 mr-2" /> Repetida
                                        </Button>
                                    )}
                                </div>

                                {/* Escalar Ticket (Moved from Admin Actions) */}
                                {!isClosed && (
                                    <Button
                                        onClick={() => setIsEscalationModalOpen(true)}
                                        className="w-full h-11 bg-indigo-600/10 border border-indigo-500/30 text-indigo-400 hover:bg-indigo-600/20 hover:text-indigo-300 rounded-xl transition-all font-bold uppercase tracking-widest text-[10px]"
                                    >
                                        <TrendingUp className="w-4 h-4 mr-2" /> ESCALAR TICKET
                                    </Button>
                                )}

                                {/* Atribuir Ticket */}
                                <div className="space-y-1.5 pt-1">
                                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest pl-1 flex items-center gap-2">
                                        ATRIBUIR TICKET
                                    </label>
                                    <Select
                                        value={ticket.assignedTo || ''}
                                        onValueChange={handleAssignTicket}
                                        disabled={isUpdating || isClosed}
                                    >
                                        <SelectTrigger className="w-full h-10 bg-black/40 border-white/10 text-[11px] rounded-xl focus:ring-emerald-500/50 text-white font-bold hover:border-white/20 transition-all">
                                            <SelectValue placeholder="Selecione um Staff" className="text-white placeholder:text-gray-400" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-gray-950 border-white/10 text-white max-h-[200px]">
                                            {allUsers.filter(u => u.accessLevel && u.accessLevel !== 'user').map((staff) => (
                                                <SelectItem key={staff.uid || staff.id} value={staff.uid || staff.id} className="text-white focus:bg-emerald-500/20">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-bold">{getStaffDisplayName(staff)}</span>
                                                        <span className="text-[9px] text-gray-400 uppercase">
                                                            ({staff.accessLevel !== 'user' ? ACCESS_LEVELS[staff.accessLevel]?.label || staff.accessLevel : 'Staff'})
                                                        </span>
                                                    </div>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            {/* 3. REPORTED PLAYER CARD */}
                            {(ticket.category === 'denuncia_jogador' || ticket.category === 'denuncia') && (
                                <div>
                                    <h4 className="text-[10px] font-bold text-gray-500 uppercase tracking-widest pl-1 mb-2">Jogador Denunciado</h4>
                                    <ReportedPlayerCard
                                        ticket={ticket}
                                        onRegisterAction={handleRegisterAction}
                                    />
                                </div>
                            )}

                            {/* 4. PROPRIEDADES (Metadata) */}
                            <div className="space-y-4 border-t border-white/5 pt-4">
                                {/* Prioridade */}
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest pl-1 flex items-center gap-2">
                                        <Activity className="w-3 h-3 text-red-400" /> PRIORIDADE DO CASO
                                    </label>
                                    <Select value={ticket.priority || 'normal'} onValueChange={handlePriorityChange} disabled={isClosed}>
                                        <SelectTrigger className="w-full h-10 bg-black/40 border-white/10 text-xs rounded-xl focus:ring-blue-500/50 text-white font-bold hover:border-white/20 transition-all [&>span]:text-white [&_[data-slot=select-value]]:text-white">
                                            <SelectValue className="text-white placeholder:text-gray-400" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-gray-950 border-white/10 text-white">
                                            <SelectItem value="baixa" className="focus:bg-green-500/20 text-white">Baixa</SelectItem>
                                            <SelectItem value="normal" className="focus:bg-blue-500/20 text-white">Normal</SelectItem>
                                            <SelectItem value="alta" className="focus:bg-orange-500/20 text-white">Alta</SelectItem>
                                            <SelectItem value="urgente" className="focus:bg-red-500/20 text-red-400">Urgente</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Setor */}
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest pl-1 flex items-center gap-2">
                                        <FolderTree className="w-3 h-3 text-blue-400" /> ENQUADRAMENTO / SETOR
                                    </label>
                                    <Select value={ticket.category} onValueChange={handleCategoryChange} disabled={isClosed}>
                                        <SelectTrigger className="w-full h-10 bg-black/40 border-white/10 text-[11px] rounded-xl focus:ring-blue-500/50 text-white font-bold hover:border-white/20 transition-all [&>span]:text-white [&_[data-slot=select-value]]:text-white">
                                            <SelectValue placeholder="SETOR" className="text-white placeholder:text-gray-400" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-gray-950 border-white/10 text-white">
                                            <SelectItem value="suporte_duvidas" className="text-white">DÚVIDAS GERAIS</SelectItem>
                                            <SelectItem value="compras_doacoes" className="text-white">PROBLEMAS COM COMPRAS</SelectItem>
                                            <SelectItem value="programacao" className="text-white">PROBLEMA TÉCNICO / BUG</SelectItem>
                                            <SelectItem value="contestar_punicao" className="text-white">CONTESTAR PUNIÇÃO</SelectItem>
                                            <SelectItem value="denuncia_jogador" className="text-white">DENUNCIAR JOGADOR</SelectItem>
                                            <SelectItem value="denuncia_staff" className="text-white">DENÚNCIA CONTRA STAFF</SelectItem>
                                            <SelectItem value="cupons_descontos" className="text-white">CUPOM OU DESCONTO</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Visibilidade */}
                                <div className="space-y-2 pt-2">
                                    <h4 className="text-[10px] font-black text-gray-500 uppercase tracking-widest pl-1 flex items-center gap-2">
                                        <Info className="w-3 h-3 text-purple-400" /> VISIBILIDADE / ACESSO
                                    </h4>
                                    <div className="bg-white/[0.02] rounded-xl p-3 border border-white/5 space-y-2">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-[10px] text-gray-400 font-medium">Equipes com Acesso</span>
                                            <Badge variant="outline" className="border-purple-500/30 text-purple-400 bg-purple-500/10 text-[9px] uppercase">
                                                {visibility.roles.length} Equipes
                                            </Badge>
                                        </div>
                                        <div className="flex flex-wrap gap-1.5 mb-4">
                                            {visibility.roles.map((role, idx) => (
                                                <Badge key={idx} className="bg-white/5 hover:bg-white/10 text-gray-300 border-none text-[9px] uppercase font-bold tracking-wider px-1.5 h-5">
                                                    {role}
                                                </Badge>
                                            ))}
                                        </div>

                                        <div className="flex items-center justify-between mb-2 pt-2 border-t border-white/5">
                                            <span className="text-[10px] text-gray-400 font-medium">Membros com Acesso</span>
                                            <Badge variant="outline" className="border-blue-500/30 text-blue-400 bg-blue-500/10 text-[9px] uppercase">
                                                {visibility.count} Membros
                                            </Badge>
                                        </div>
                                        <div className="max-h-[160px] overflow-y-auto pr-2 custom-scrollbar space-y-1.5">
                                            {visibility.members.map((member, idx) => (
                                                <div key={idx} className="flex items-center justify-between p-2 rounded-lg bg-black/40 border border-white/5 hover:border-white/10 transition-all group/member">
                                                    <div className="flex flex-col min-w-0">
                                                        <span className="text-[10px] font-bold text-white truncate group-hover/member:text-blue-400 transition-colors">
                                                            {member.name}
                                                        </span>
                                                        <span className="text-[9px] text-gray-500 font-medium uppercase tracking-wider">
                                                            {member.role === 'Usuário' ? 'Autor' : member.role}
                                                        </span>
                                                    </div>
                                                    <span className="text-[8px] font-mono text-gray-600 group-hover/member:text-gray-400 transition-colors ml-2 shrink-0">
                                                        {member.id}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* 5. DEMAIS RECURSOS */}
                            {/* Ação Aplicada */}
                            {ticket.category === 'acao_disciplinar' && ticket.formData?.isPunishmentRequest && !isClosed && (
                                <div className="pt-2 border-t border-white/5">
                                    <Button
                                        onClick={handleApplyPunishment}
                                        className="w-full h-11 bg-gradient-to-r from-red-600 to-orange-600 hover:scale-[1.02] text-white border-none shadow-lg shadow-red-900/40 rounded-xl font-bold text-[10px] uppercase tracking-wider"
                                    >
                                        <Gavel className="w-4 h-4 mr-2" /> AÇÃO APLICADA
                                    </Button>
                                </div>
                            )}

                            {/* 6. AÇÕES ADMINISTRATIVAS (Delete) */}
                            {user.accessLevel === 'admin' && (
                                <div className="pt-4 border-t border-white/5 space-y-2">
                                    <h4 className="text-[10px] font-bold text-gray-500 uppercase tracking-widest pl-1">Ações Administrativas</h4>
                                    <Button
                                        variant="ghost"
                                        onClick={handleDeleteTicket}
                                        disabled={isUpdating}
                                        className="w-full h-10 text-gray-500 hover:text-red-500 hover:bg-red-500/10 rounded-xl transition-all border border-transparent hover:border-red-500/20 font-bold uppercase tracking-widest text-[10px] flex justify-start pl-4"
                                    >
                                        <Trash2 className="w-4 h-4 mr-2" /> EXCLUIR TICKET (ADMIN)
                                    </Button>
                                </div>
                            )}

                        </div>
                    )}

                    {/* === TAB: DETALHES (Novo) === */}
                    {activeTab === 'details' && userDetails && (
                        <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">

                            {/* User Header Info - Mantido estilo premium */}
                            <div className="flex flex-col items-center p-4 bg-white/[0.02] border border-white/5 rounded-2xl">
                                <div className="w-16 h-16 rounded-full border-2 border-emerald-500/30 p-0.5 mb-3 shadow-[0_0_15px_rgba(16,185,129,0.15)]">
                                    <img
                                        src={userDetails.avatar ? `https://cdn.discordapp.com/avatars/${userDetails.discordId}/${userDetails.avatar}.png` : 'https://github.com/shadcn.png'}
                                        alt={userDetails.name}
                                        className="w-full h-full rounded-full object-cover"
                                    />
                                </div>
                                <h3 className="text-sm font-bold text-white mb-1">{userDetails.name}</h3>

                                {/* Discord ID Copyable */}
                                <div
                                    className="flex items-center gap-1.5 text-[10px] text-gray-500 hover:text-gray-300 cursor-pointer transition-colors mb-2 bg-black/40 px-2 py-0.5 rounded-md border border-white/5 hover:border-white/10"
                                    onClick={() => {
                                        navigator.clipboard.writeText(userDetails.discordId);
                                        toast.success("ID copiado para a área de transferência!");
                                    }}
                                    title="Clique para copiar ID"
                                >
                                    <span className="font-mono">{userDetails.discordId}</span>
                                    <Copy className="w-2.5 h-2.5" />
                                </div>

                                {activeTicketBan ? (
                                    <div className="w-full bg-red-500/10 border border-red-500/30 rounded-xl p-3 mb-4 text-center">
                                        <div className="flex items-center justify-center gap-2 mb-2">
                                            <Lock className="w-4 h-4 text-red-500" />
                                            <span className="text-[10px] font-black text-red-400 uppercase tracking-widest">BLOQUEADO DE TICKETS</span>
                                        </div>
                                        <div className="text-[10px] text-gray-400 italic mb-3 leading-tight px-1">
                                            "{activeTicketBan.internalReason || activeTicketBan.publicReason}"
                                        </div>
                                        <Button
                                            onClick={handleUnblockTickets}
                                            variant="ghost"
                                            className="h-8 bg-red-500 hover:bg-red-400 text-white w-full rounded-lg text-[9px] font-black uppercase tracking-widest"
                                        >
                                            DESBLOQUEAR
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="w-full mb-4">
                                        <Button
                                            onClick={handleBlockTickets}
                                            variant="ghost"
                                            className="w-full h-9 bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 border border-orange-500/30 rounded-xl text-[9px] font-black uppercase tracking-widest flex items-center justify-center gap-2"
                                        >
                                            <Shield className="w-3 h-3" /> BLOQUEAR TICKETS
                                        </Button>
                                    </div>
                                )}

                                <div className="flex gap-2 mb-3">
                                    <Badge className="bg-blue-500/20 text-blue-400 border-0 text-[9px] uppercase hover:bg-blue-500/30">
                                        {ACCESS_LEVELS[userDetails.accessLevel]?.label || userDetails.accessLevel}
                                    </Badge>
                                    <Badge className="bg-white/5 text-gray-400 border-0 text-[9px] uppercase">
                                        {userDetails.stats?.totalTickets} Tickets
                                    </Badge>
                                </div>
                            </div>

                            {/* Personagens */}
                            <div className="space-y-3">
                                <h4 className="text-[10px] font-bold text-gray-500 uppercase tracking-widest pl-1">Personagens Vinculados</h4>
                                {userDetails.characters && userDetails.characters.length > 0 ? (
                                    <div className="space-y-2">
                                        {userDetails.characters.map((char, i) => (
                                            <div key={i} className="bg-black/60 border border-white/10 p-3 rounded-xl flex flex-col group hover:border-white/20 transition-all gap-2">
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <span className="text-[11px] font-bold text-gray-300">{char.characterName}</span>
                                                            <Badge variant="outline" className="text-[8px] h-4 px-1 border-white/10 text-gray-500">{char.city || 'N/A'}</Badge>
                                                        </div>
                                                        <span className="text-[9px] font-mono text-gray-600">ID: {char.playerId || 'N/A'}</span>
                                                    </div>

                                                    {/* Status Badge */}
                                                    <div>
                                                        {char.status === 'verified' ? (
                                                            <Badge className="bg-emerald-500/10 text-emerald-400 border-0 h-5 text-[8px] uppercase font-bold px-1.5">Aprovado</Badge>
                                                        ) : char.status === 'pending' || char.status === 'pending_approval' ? (
                                                            <Badge className="bg-yellow-500/10 text-yellow-400 border-0 h-5 text-[8px] uppercase font-bold px-1.5">Pendente</Badge>
                                                        ) : (
                                                            <Badge className="bg-gray-500/10 text-gray-400 border-0 h-5 text-[8px] uppercase font-bold px-1.5">Inativo</Badge>
                                                        )}
                                                    </div>
                                                </div>

                                                {char.activePunishments > 0 && (
                                                    <div className="flex items-center gap-2 pt-2 border-t border-white/5 mt-1">
                                                        <Badge className="bg-red-500/10 text-red-400 border-red-500/20 text-[9px] h-5 gap-1 pl-1 pr-2">
                                                            <Activity className="w-2.5 h-2.5" />
                                                            {char.activePunishments} Punição(ões)
                                                        </Badge>
                                                        {char.lastPunishmentAuthor && (
                                                            <span className="text-[8px] text-gray-500 uppercase tracking-wide">
                                                                Por: {char.lastPunishmentAuthor}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center p-4 border border-dashed border-white/10 rounded-xl text-gray-600 text-[10px]">
                                        Nenhum personagem vinculado
                                    </div>
                                )}
                            </div>

                            {/* Histórico Global (10 Ações) */}
                            <div className="space-y-3">
                                <h4 className="text-[10px] font-bold text-gray-500 uppercase tracking-widest pl-1">Histórico Global da Conta</h4>
                                <div className="space-y-2">
                                    {userDetails.recentActions && userDetails.recentActions.length > 0 ? (
                                        userDetails.recentActions.map(action => (
                                            <div
                                                key={action.id}
                                                className={`bg-black/40 border-l-2 p-3 rounded-r-xl flex items-start gap-3 transition-all hover:bg-white/[0.02] border-white/5`}
                                                style={{ borderLeftColor: action.color === 'red' ? '#ef4444' : action.color === 'green' ? '#10b981' : '#3b82f6' }}
                                            >
                                                <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0 bg-${action.color === 'red' ? 'red' : action.color === 'green' ? 'emerald' : 'blue'}-500/10`}>
                                                    {action.icon === 'gavel' && <Gavel className={`w-3 h-3 text-${action.color === 'red' ? 'red' : 'gray'}-400`} />}
                                                    {action.icon === 'check' && <CheckCircle className="w-3 h-3 text-emerald-400" />}
                                                    {action.icon === 'message' && <MessageCircle className="w-3 h-3 text-blue-400" />}
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex justify-between items-center mb-0.5">
                                                        <p className="text-[10px] font-bold text-gray-200 truncate pr-2">{action.title}</p>
                                                        <span className="text-[8px] text-gray-500 font-mono whitespace-nowrap">{action.date.toLocaleDateString()}</span>
                                                    </div>
                                                    <p className="text-[9px] text-gray-500 truncate">{action.description}</p>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-center p-4 text-gray-600 text-[10px]">
                                            Sem ações recentes
                                        </div>
                                    )}
                                </div>
                            </div>

                        </div>
                    )}

                    {/* === TAB: LOGS === */}
                    {activeTab === 'logs' && (
                        <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
                            {/* Conteúdo direto sem título repetitivo */}
                            <div className="bg-black/20 rounded-2xl p-4 border border-white/5 max-h-[600px] overflow-y-auto custom-scrollbar space-y-3">
                                {ticket.logs && ticket.logs.length > 0 ? (
                                    ticket.logs.slice().reverse().map((log, index) => (
                                        <div key={log.id || index} className="flex gap-3 relative pb-6 last:pb-0 border-l border-white/5 pl-4 last:border-0">
                                            {/* Timeline dot */}
                                            <div className={`absolute left-[-5px] top-1.5 w-2.5 h-2.5 rounded-full border-2 ${log.type === 'creation' ? 'bg-blue-500 border-blue-900' :
                                                log.type === 'response' ? 'bg-purple-500 border-purple-900' :
                                                    log.type === 'claim' ? 'bg-emerald-500 border-emerald-900' :
                                                        log.type === 'assignment' ? 'bg-orange-500 border-orange-900' :
                                                            log.type === 'update' ? 'bg-yellow-500 border-yellow-900' :
                                                                'bg-gray-500 border-gray-900'
                                                }`}></div>

                                            <div className="flex-1 space-y-1">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-[10px] font-bold text-white/90">
                                                        {log.userName}
                                                    </span>
                                                    <span className="text-[9px] text-gray-500 font-mono">
                                                        {new Date(log.timestamp).toLocaleDateString()} {new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                    </span>
                                                </div>
                                                <p className="text-[10px] text-gray-400 leading-relaxed">
                                                    {log.message}
                                                </p>
                                                <div className="flex items-center gap-2">
                                                    <Badge variant="outline" className={`text-[8px] px-1.5 py-0 h-4 border-none bg-white/5 text-gray-500`}>
                                                        {log.userRole === 'user' ? 'USUÁRIO' : 'STAFF'}
                                                    </Badge>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center py-6 text-gray-500">
                                        <Activity className="w-8 h-8 mx-auto mb-2 opacity-20" />
                                        <p className="text-[10px] uppercase font-bold tracking-widest">Nenhum registro</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Collapsed State Icons (Show when closed) */}
                {!isOpen && (
                    <div className="flex flex-col items-center gap-4 mt-4">
                        <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center border border-blue-500/20" title="Controle Staff">
                            <ShieldCheck className="w-4 h-4 text-blue-400" />
                        </div>
                        {ticket.priority === 'urgente' && (
                            <div className="w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center border border-red-500/20 animate-pulse" title="Urgente">
                                <Activity className="w-4 h-4 text-red-400" />
                            </div>
                        )}
                    </div>
                )}
            </div>
            {/* Modal de Bloqueio de Tickets */}
            <Dialog open={isBlockModalOpen} onOpenChange={setIsBlockModalOpen}>
                <DialogContent className="bg-[#121214] border-white/10 text-white max-w-md rounded-[2rem]">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-500 font-black uppercase text-lg">
                            <Shield className="w-5 h-5" /> Bloquear Tickets
                        </DialogTitle>
                        <DialogDescription className="text-gray-400 text-xs">
                            O usuário será impedido de abrir novos tickets. Esta ação requer um motivo interno obrigatório para auditoria.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-3 py-2">
                        <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest pl-1">Motivo Interno (Obrigatório)</label>
                        <Textarea
                            value={blockReason}
                            onChange={(e) => setBlockReason(e.target.value)}
                            placeholder="Descreva o motivo do bloqueio..."
                            className="bg-black/40 border-white/10 min-h-[100px] text-sm focus:border-red-500/50"
                        />
                    </div>

                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setIsBlockModalOpen(false)}>Cancelar</Button>
                        <Button
                            onClick={confirmBlockTickets}
                            disabled={isBlocking || !blockReason.trim()}
                            className="bg-red-600 hover:bg-red-500 text-white font-bold uppercase tracking-widest"
                        >
                            {isBlocking ? 'Bloqueando...' : 'Confirmar Bloqueio'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            {/* Modal de Escalação */}
            <Dialog open={isEscalationModalOpen} onOpenChange={setIsEscalationModalOpen}>
                <DialogContent className="bg-[#0f111a] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto custom-scrollbar">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-xl">
                            <TrendingUp className="w-6 h-6 text-indigo-400" />
                            Escalar Ticket
                        </DialogTitle>
                        <DialogDescription className="text-gray-400">
                            Preencha os dados abaixo para gerar um ticket de escalação vinculado.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-6 py-4">
                        {/* 1. Categoria */}
                        <div className="space-y-3">
                            <Label className="text-gray-300 font-bold">Categoria da Escalação</Label>
                            <div className="grid grid-cols-2 gap-3">
                                {escalationCategories.map((cat) => (
                                    <div
                                        key={cat.id}
                                        onClick={() => setEscalationData(prev => ({ ...prev, category: cat.id, subcategory: '' }))}
                                        className={`p-3 rounded-lg border cursor-pointer transition-all flex items-center gap-3 ${escalationData.category === cat.id
                                            ? `border-${cat.color}-500 bg-${cat.color}-500/10`
                                            : 'border-white/10 bg-white/5 hover:border-white/20'
                                            }`}
                                    >
                                        <cat.icon className={`h-5 w-5 ${escalationData.category === cat.id ? `text-${cat.color}-400` : 'text-gray-400'}`} />
                                        <span className={`text-sm font-medium ${escalationData.category === cat.id ? 'text-white' : 'text-gray-300'}`}>
                                            {cat.label}
                                        </span>
                                        {escalationData.category === cat.id && <CheckCircle className={`ml-auto w-4 h-4 text-${cat.color}-400`} />}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* 2. Subcategoria (Motivo) */}
                        {escalationData.category && (
                            <div className="space-y-3 animate-in fade-in slide-in-from-top-2">
                                <Label className="text-gray-300 font-bold">Motivo Específico</Label>
                                <Select
                                    value={escalationData.subcategory}
                                    onValueChange={(val) => setEscalationData(prev => ({ ...prev, subcategory: val }))}
                                >
                                    <SelectTrigger className="bg-black/40 border-white/10 text-white h-11">
                                        <SelectValue placeholder="Selecione o motivo..." />
                                    </SelectTrigger>
                                    <SelectContent className="bg-gray-950 border-white/10 text-white">
                                        {escalationCategories.find(c => c.id === escalationData.category)?.subcategories.map((sub) => (
                                            <SelectItem key={sub} value={sub} className="text-white hover:bg-white/10">
                                                {sub}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {/* 3. Descrição */}
                        <div className="space-y-3">
                            <Label className="text-gray-300 font-bold">Descrição Detalhada</Label>
                            <Textarea
                                placeholder="Descreva os detalhes técnicos ou o motivo da escalação..."
                                value={escalationData.description}
                                onChange={(e) => setEscalationData(prev => ({ ...prev, description: e.target.value }))}
                                className="bg-black/40 border-white/10 text-white min-h-[120px] focus:ring-indigo-500/50"
                            />
                        </div>

                        {/* 4. Departamento */}
                        <div className="space-y-3">
                            <Label className="text-gray-300 font-bold">Departamento Responsável</Label>
                            <div className="grid grid-cols-2 gap-2">
                                {availableRoles.map((role) => (
                                    <div
                                        key={role.value}
                                        onClick={() => setEscalationData(prev => ({ ...prev, role: role.value }))}
                                        className={`flex items-center space-x-2 p-2 rounded-lg border cursor-pointer transition-all ${escalationData.role === role.value
                                            ? 'border-indigo-500 bg-indigo-500/10'
                                            : 'border-white/10 bg-white/5 hover:border-white/20'
                                            }`}
                                    >
                                        <div className={`w-3 h-3 rounded-full border flex items-center justify-center flex-shrink-0 ${escalationData.role === role.value ? 'border-indigo-500' : 'border-gray-500'}`}>
                                            {escalationData.role === role.value && <div className="w-1.5 h-1.5 rounded-full bg-indigo-500" />}
                                        </div>
                                        <span className="text-xs text-white">{role.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Resumo */}
                        {escalationData.category && escalationData.subcategory && escalationData.role && (
                            <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-lg p-4 animate-in fade-in slide-in-from-bottom-4">
                                <h4 className="text-xs font-bold text-indigo-400 uppercase tracking-widest mb-2 flex items-center">
                                    <Info className="w-4 h-4 mr-2" /> Resumo da Escalação
                                </h4>
                                <div className="space-y-1 text-xs text-gray-300">
                                    <p><span className="text-gray-500">Origem:</span> Ticket #{ticket.id}</p>
                                    <p><span className="text-gray-500">Categoria:</span> {escalationCategories.find(c => c.id === escalationData.category)?.label}</p>
                                    <p><span className="text-gray-500">Motivo:</span> {escalationData.subcategory}</p>
                                    <p><span className="text-gray-500">Destino:</span> {availableRoles.find(r => r.value === escalationData.role)?.label}</p>
                                </div>
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setIsEscalationModalOpen(false)}
                            className="border-white/10 text-gray-400 hover:text-white hover:bg-white/10"
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleEscalateTicket}
                            disabled={!escalationData.category || !escalationData.subcategory || !escalationData.description || !escalationData.role || isEscalating}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white border-none shadow-lg shadow-indigo-500/20"
                        >
                            {isEscalating ? 'Gerando Escalação...' : 'Confirmar Escalação'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

        </>
    );
};

export default StaffTicketSidebar;
